import { useState, useEffect, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { GAME_STAGES, DEFAULT_ROOM } from "../../constants/gameStages";
import socket from "../../socket/socket";
import { useGameDataSync } from "../../hooks/useGameDataSync";
import { SocketContext } from "../../context/SocketProvider";
import { useRoleBackground } from "../../hooks/useRoleBackground";
import HowToPlay from "../../components/HowToPlay";

function ShowGamemode() {
  const { state } = useLocation();
  const mode = state?.mode;
  const { role, gameStage } = useContext(SocketContext);
  const { gameData } = useGameDataSync();
  const [hoveredGamemode, setHoveredGamemode] = useState(null); // only local
  const [confirmedGamemode, setConfirmedGamemode] = useState(mode || null); // Initialize with mode if available
  const [isLoadingGamemode, setIsLoadingGamemode] = useState(!mode); // Loading if we don't have mode
  
  // Use confirmedGamemode if mode is undefined (for guesser)
  const activeGamemode = mode || confirmedGamemode;
  
  const destination =
    role === "describer"
      ? activeGamemode === "normal"
        ? "/pickingimagenormal"
        : "/pickingimage"
      : gameStage === GAME_STAGES.WAITING_FOR_GUESSER && gameData?.description
      ? "/guesserguess"
      : "/waitingfordescriber";

  const navigate = useNavigate();
  const { bgStyle, className } = useRoleBackground(
    role === "describer" ? "pink" : "blue"
  );
  const [step, setStep] = useState(1);

  // On mount, guesser requests the current mode
  useEffect(() => {
    socket.emit("get_gamemode", { room: DEFAULT_ROOM });
  });

  // Describer responds if mode already selected
  useEffect(() => {
    const handleGetGamemode = () => {
      if (role === "describer" && confirmedGamemode) {
        socket.emit("gamemode_selected", {
          mode: confirmedGamemode,
          room: DEFAULT_ROOM,
        });
      }
    };
    socket.on("get_gamemode", handleGetGamemode);
    return () => socket.off("get_gamemode", handleGetGamemode);
  }, [role, confirmedGamemode]);

  // Receive selected mode from describer
  useEffect(() => {
    const handleGamemodeSelected = (data) => {
      if (data?.mode) {
        setConfirmedGamemode(data.mode);
        setIsLoadingGamemode(false);
        console.log("✅ Confirmed gamemode received:", data.mode);
      }
    };
    socket.on("gamemode_selected", handleGamemodeSelected);
    return () => socket.off("gamemode_selected", handleGamemodeSelected);
  }, []);

  // Reset mode if returning to home
  useEffect(() => {
    if (gameStage === GAME_STAGES.WAITING || gameStage === GAME_STAGES.READY) {
      setConfirmedGamemode(null);
      setHoveredGamemode(null); // optional: clear visual highlight too
    }
  }, [gameStage]);

  const handleSkip = () => {
    navigate(destination);
  };

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  return (
    <div
      className={`flex flex-col justify-center items-center ${className}`}
      style={bgStyle}
    >
      {isLoadingGamemode ? (
        <div className="text-white text-2xl">Loading gamemode...</div>
      ) : (
        <HowToPlay
          onSkip={handleSkip}
          onNext={handleNext}
          onPrev={handleBack}
          step={step}
          gamemode={activeGamemode}
        />
      )}
    </div>
  );
}

export default ShowGamemode;
